'use client';

import { useQuery } from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { useCallback, useState, useEffect } from 'react';
import clsx from 'clsx';
import type { SliderItem } from '@shared/types/schema';
import { CarouselNavButton } from './games/carousel-nav-button';
import { AnimatePresence, motion } from 'framer-motion';
import { fetcher } from '@/lib/queryClient';
import { EmblaCarouselType } from 'embla-carousel';
import { HomeGame } from '@/types/game';
import { NonUndefined } from 'react-hook-form';

function isGameSlide(slide: SliderItem): slide is SliderItem & { game: HomeGame } {
  return slide.type === 'game' && typeof (slide as any).game === 'object' && !!(slide as any).game;
}

type AnimatedNavButtonProps = {
  direction: 'prev' | 'next';
  onClick: () => void;
};

function AnimatedNavButton({ direction, onClick }: AnimatedNavButtonProps) {
  const isPrev = direction === 'prev';
  return (
    <motion.div
      key={direction}
      initial={{ opacity: 0, x: isPrev ? -24 : 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: isPrev ? -24 : 24 }}
      transition={{ duration: 0.2 }}
      className={
        isPrev
          ? 'absolute left-2 top-1/2 -translate-y-1/2 z-10'
          : 'absolute right-2 top-1/2 -translate-y-1/2 z-10'
      }
    >
      <CarouselNavButton direction={direction} onClick={onClick} />
    </motion.div>
  );
}

type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselPlugin = UseCarouselParameters[1];

export default function HeroSlider() {
  const autoplayPlugin = Autoplay({
    playOnInit: true,
    delay: 5000,
    stopOnInteraction: false,
  });

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
      containScroll: false,
    },
    [autoplayPlugin as unknown as NonUndefined<CarouselPlugin>[0]],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const { data: slides = [] } = useQuery<SliderItem[]>({
    queryKey: ['/api/slider'],
    queryFn: fetcher,
  });

  const activeSlides = slides.filter((slide) => slide.isActive);
  const canScrollPrev = selectedIndex > 0;
  const canScrollNext = selectedIndex < scrollSnaps.length - 1;

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onInit = useCallback((api: EmblaCarouselType) => {
    setScrollSnaps(api.scrollSnapList());
  }, []);
  const onSelect = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  const getBackgroundImage = (
    slide: SliderItem & { game?: HomeGame | null },
  ): string | undefined => {
    if (slide.type === 'image') {
      return slide.imageUrl ? `url(${slide.imageUrl})` : undefined;
    }
    const imageUrl = slide.game?.thumbnailUrl || slide.game?.backgroundUrl;
    return imageUrl ? `url(${imageUrl})` : undefined;
  };

  return (
    <div className="relative h-full min-h-full group">
      <div className="overflow-visible h-full rounded-lg" ref={emblaRef}>
        <div className="flex h-full">
          {activeSlides.map((slide, idx) => {
            const isActive = idx === selectedIndex;
            const SlideContent = (
              <div
                className={clsx(
                  'h-full relative overflow-hidden rounded-lg aspect-video max-h-[70vh]',
                  'cursor-pointer',
                  !isActive && 'opacity-50',
                )}
                style={{
                  backgroundImage: getBackgroundImage(slide),
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                key={`slide-${slide.id ?? idx}-${slide.createdAt ?? ''}`}
              />
            );

            const commonProps = {
              className:
                'block flex-[0_0_90%] md:flex-[0_0_80%] lg:flex-[0_0_55%] min-w-0 h-full mx-1',
              onClick: (e: React.MouseEvent) => {
                if (idx !== selectedIndex) {
                  e.preventDefault();
                  emblaApi?.scrollTo(idx);
                }
              },
            };

            if (slide.linkUrl) {
              return (
                <a key={slide.id} href={slide.linkUrl} {...commonProps}>
                  {SlideContent}
                </a>
              );
            } else if (isGameSlide(slide)) {
              return (
                <a key={slide.id} href={`/game/${slide.gameId}`} {...commonProps}>
                  {SlideContent}
                </a>
              );
            } else {
              return (
                <div
                  key={slide.id}
                  className="flex-[0_0_90%] md:flex-[0_0_80%] lg:flex-[0_0_55%] h-full mx-1"
                >
                  {SlideContent}
                </div>
              );
            }
          })}
        </div>
      </div>

      {activeSlides.length > 1 && (
        <>
          <AnimatePresence>
            {canScrollPrev && (
              <AnimatedNavButton key="prev" direction="prev" onClick={scrollPrev} />
            )}
          </AnimatePresence>
          <AnimatePresence>
            {canScrollNext && (
              <AnimatedNavButton key="next" direction="next" onClick={scrollNext} />
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}
